import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  MapPin,
  Warehouse,
  Grid3X3
} from 'lucide-react';

export default function StorageLocationsPage() {
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  
  const [formData, setFormData] = useState({
    warehouse_id: '',
    code: '',
    name: '',
    zone: '',
    aisle: '',
    rack: '',
    shelf: '',
    bin: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [filterWarehouse]);

  const fetchData = async () => {
    try {
      const warehousesRes = await api.get('/warehouses');
      setWarehouses(warehousesRes.data);
      await fetchLocations();
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const params = filterWarehouse !== 'all' ? `?warehouse_id=${filterWarehouse}` : '';
      const locationsRes = await api.get(`/storage-locations${params}`);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.warehouse_id || !formData.code || !formData.name) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      await api.post('/storage-locations', formData);
      toast.success('Storage location created!');
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create location');
    }
  };

  const resetForm = () => {
    setFormData({
      warehouse_id: '',
      code: '',
      name: '',
      zone: '',
      aisle: '',
      rack: '',
      shelf: '',
      bin: ''
    });
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString();

  return (
    <div className="space-y-6" data-testid="storage-locations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">STORAGE LOCATIONS</h1>
          <p className="text-[#0A0A0A]/60 mt-1">Manage bins, racks, and zones within warehouses</p>
        </div>
        <Button className="bg-[#007AFF] hover:bg-[#0066CC]" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-location-btn">
          <Plus className="w-4 h-4 mr-2" />New Location
        </Button>
      </div>

      {/* Filter */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-sm whitespace-nowrap">Filter by Warehouse:</Label>
            <Select value={filterWarehouse} onValueChange={setFilterWarehouse}>
              <SelectTrigger className="w-64" data-testid="warehouse-filter">
                <SelectValue placeholder="All Warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {warehouses.map((wh) => (
                  <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Locations Table */}
      <Card className="border border-[#E5E5EA] shadow-none">
        <CardHeader className="border-b border-[#E5E5EA] pb-4">
          <CardTitle className="text-heading text-lg flex items-center gap-2">
            <Grid3X3 className="w-5 h-5 text-[#007AFF]" />
            Storage Locations ({locations.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
            </div>
          ) : locations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <MapPin className="w-12 h-12 text-[#0A0A0A]/20 mb-4" />
              <p className="text-[#0A0A0A]/60">No storage locations defined</p>
              <Button className="mt-4 bg-[#007AFF]" onClick={() => { resetForm(); setDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />Create First Location
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#E5E5EA]">
                  <TableHead className="text-overline text-[#0A0A0A]/50">Code</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Name</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Warehouse</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Path</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50 text-center">Items</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Status</TableHead>
                  <TableHead className="text-overline text-[#0A0A0A]/50">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.map((loc) => (
                  <TableRow key={loc.id} className="border-b border-[#E5E5EA]" data-testid={`location-row-${loc.id}`}>
                    <TableCell className="font-mono font-medium">{loc.code}</TableCell>
                    <TableCell className="font-medium">{loc.name}</TableCell>
                    <TableCell className="text-[#0A0A0A]/60">{loc.warehouse_name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-[#F5F5F7] px-2 py-1 rounded">{loc.full_path}</code>
                    </TableCell>
                    <TableCell className="text-center font-mono">{loc.items_count}</TableCell>
                    <TableCell>
                      <Badge className={loc.is_active ? 'bg-[#34C759]/10 text-[#34C759]' : 'bg-[#FF3B30]/10 text-[#FF3B30]'}>
                        {loc.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#0A0A0A]/60">{formatDate(loc.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Location Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-heading text-xl flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#007AFF]" />
              New Storage Location
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Warehouse *</Label>
              <Select value={formData.warehouse_id} onValueChange={(v) => setFormData({ ...formData, warehouse_id: v })}>
                <SelectTrigger data-testid="location-warehouse-select">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((wh) => (
                    <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., A-01-02"
                  required
                  data-testid="location-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Aisle A Rack 1"
                  required
                  data-testid="location-name-input"
                />
              </div>
            </div>

            <div className="p-3 bg-[#F5F5F7] rounded-lg space-y-3">
              <Label className="text-sm font-medium">Location Hierarchy (Optional)</Label>
              <div className="grid grid-cols-5 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-[#0A0A0A]/50">Zone</Label>
                  <Input
                    value={formData.zone}
                    onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                    placeholder="A"
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#0A0A0A]/50">Aisle</Label>
                  <Input
                    value={formData.aisle}
                    onChange={(e) => setFormData({ ...formData, aisle: e.target.value })}
                    placeholder="01"
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#0A0A0A]/50">Rack</Label>
                  <Input
                    value={formData.rack}
                    onChange={(e) => setFormData({ ...formData, rack: e.target.value })}
                    placeholder="02"
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#0A0A0A]/50">Shelf</Label>
                  <Input
                    value={formData.shelf}
                    onChange={(e) => setFormData({ ...formData, shelf: e.target.value })}
                    placeholder="03"
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-[#0A0A0A]/50">Bin</Label>
                  <Input
                    value={formData.bin}
                    onChange={(e) => setFormData({ ...formData, bin: e.target.value })}
                    placeholder="04"
                    className="text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-[#0A0A0A]/50">
                Path preview: {[formData.zone, formData.aisle, formData.rack, formData.shelf, formData.bin].filter(Boolean).join(' / ') || formData.name || '-'}
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#007AFF]" data-testid="create-location-btn">Create Location</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
